import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  FontFamily,
  imageElementSchema,
  richTextElementSchema,
} from "../../content-builder/element-schema";

export const LOYALTY_PAGE_MAX_WIDTH = 600;

export enum LoyaltyElementType {
  HERO = "hero",
  POINTS_SUMMARY = "points-summary",
  PROMOTIONS = "promotions",
  EARNING_RULES = "earning-rules",
}

const baseBlockSchema = z.object({
  id: z.string(),
  headerFontFamily: createEnum(FontFamily, { enumName: "FontFamily" }),
  bodyFontFamily: createEnum(FontFamily, { enumName: "FontFamily" }),
});

export enum HeroBlockLogoType {
  COMPANY_LOGO = "company-logo",
  TROPHY = "trophy",
  STAR = "star",
  GIFT = "gift",
  HEART = "heart",
  NONE = "none",
}

const heroBlockSchema = baseBlockSchema.extend({
  type: z.literal(LoyaltyElementType.HERO),
  visible: z.boolean(),
  copy: z.object({
    loggedIn: z.object({
      title: z.string(),
      titleColor: z.string(),
      subtitle: z.string(),
      subtitleColor: z.string(),
    }),
    loggedOut: z.object({
      title: z.string(),
      titleColor: z.string(),
      subtitle: z.string(),
      subtitleColor: z.string(),
    }),
  }),
  background: z.object({
    imageUrl: z.string(),
    imageOpacity: z.number().min(0).max(1),
    fallbackColor: z.string(),
  }),
  logoType: createEnum(HeroBlockLogoType, { enumName: "HeroBlockLogoType" }),
});
export type HeroBlock = z.infer<typeof heroBlockSchema>;

export enum PointsSummaryButtonStyle {
  FILLED = "filled",
  OUTLINED = "outlined",
  GHOST = "ghost",
}

const pointsSummaryBlockSchema = baseBlockSchema.extend({
  type: z.literal(LoyaltyElementType.POINTS_SUMMARY),
  visible: z.boolean(),
  loggedOutContent: z.object({
    title: z.string(),
    titleColor: z.string(),
    body: z.string(),
    bodyColor: z.string(),
    buttonStyle: createEnum(PointsSummaryButtonStyle, {
      enumName: "PointsSummaryButtonStyle",
    }),
    buttonText: z.string(),
    buttonLink: z.string(),
    buttonBackgroundColor: z.string(),
    buttonTextColor: z.string(),
    buttonBorderColor: z.string(),
    buttonBorderRadius: z.number(),
  }),
  loggedInContent: z.object({
    title: z.string(),
    titleColor: z.string(),
    body: z.string(),
    bodyColor: z.string(),
  }),
  allTime: z.object({
    pointsEarned: z.object({ enabled: z.boolean(), color: z.string() }),
    moneySpent: z.object({ enabled: z.boolean(), color: z.string() }),
    pointsSpent: z.object({ enabled: z.boolean(), color: z.string() }),
  }),
  pointsBalance: z.object({ color: z.string() }),
});
export type PointsSummaryBlock = z.infer<typeof pointsSummaryBlockSchema>;

export enum LoyaltyBorderStyle {
  BORDER = "border",
  NO_BORDER = "no-border",
}

export enum EmptyStateIcon {
  GIFT = "gift",
  STAR = "star",
  TROPHY = "trophy",
  HEART = "heart",
  CALENDAR = "calendar",
  NONE = "none",
}

const promotionsBlockSchema = baseBlockSchema.extend({
  type: z.literal(LoyaltyElementType.PROMOTIONS),
  visible: z.boolean(),
  title: z.string(),
  titleColor: z.string(),
  subtitle: z.string(),
  subtitleColor: z.string(),
  promotions: z.object({ titleColor: z.string(), contentColor: z.string() }),
  borderStyle: createEnum(LoyaltyBorderStyle, {
    enumName: "LoyaltyBorderStyle",
  }),
  emptyState: z.object({
    icon: createEnum(EmptyStateIcon, { enumName: "EmptyStateIcon" }),
    message: z.string(),
    messageColor: z.string(),
  }),
});
export type PromotionsBlock = z.infer<typeof promotionsBlockSchema>;

const earningRulesBlockSchema = baseBlockSchema.extend({
  type: z.literal(LoyaltyElementType.EARNING_RULES),
  visible: z.boolean(),
  title: z.string(),
  titleColor: z.string(),
  subtitle: z.string(),
  subtitleColor: z.string(),
  earningRules: z.object({
    titleColor: z.string(),
    subtitleColor: z.string(),
    pointsColor: z.string(),
  }),
  borderStyle: createEnum(LoyaltyBorderStyle, {
    enumName: "LoyaltyBorderStyle",
  }),
});
export type EarningRulesBlock = z.infer<typeof earningRulesBlockSchema>;

const loyaltyRichTextBlockSchema = richTextElementSchema
  .omit({ id: true })
  .extend({ id: z.string() });
export type LoyaltyRichTextBlock = z.infer<typeof loyaltyRichTextBlockSchema>;

const loyaltyImageBlockSchema = imageElementSchema.extend({ id: z.string() });
export type LoyaltyImageBlock = z.infer<typeof loyaltyImageBlockSchema>;

const loyaltyPageBlockSchema = z.discriminatedUnion("type", [
  heroBlockSchema,
  pointsSummaryBlockSchema,
  promotionsBlockSchema,
  earningRulesBlockSchema,
  loyaltyRichTextBlockSchema,
  loyaltyImageBlockSchema,
]);
export type LoyaltyPageBlock = z.infer<typeof loyaltyPageBlockSchema>;

export const loyaltyPageSettingsSchema = z.object({
  blocks: z.array(loyaltyPageBlockSchema),
});
export type LoyaltyPageSettings = z.infer<typeof loyaltyPageSettingsSchema>;

export function validateLoyaltyPageSettings(
  settings: LoyaltyPageSettings,
): boolean {
  const validationResult = loyaltyPageSettingsSchema.safeParse(settings);
  if (!validationResult.success) return false;

  const requiredTypes = [
    LoyaltyElementType.HERO,
    LoyaltyElementType.POINTS_SUMMARY,
    LoyaltyElementType.PROMOTIONS,
    LoyaltyElementType.EARNING_RULES,
  ];

  const indices = requiredTypes.map((type) =>
    settings.blocks.findIndex((block) => block.type === type),
  );

  if (indices.includes(-1)) {
    return false;
  }

  for (let i = 1; i < indices.length; i++) {
    if (indices[i] < indices[i - 1]) {
      return false;
    }
  }

  return true;
}
